import { useState } from "react";
import CandidateQuickActionButtons from "./CandidateQuickActionButtons";
import { completedTestTypeForStatus, isArchiveStatus, statusLabel, statusTone } from "../../utils/candidateStatuses";
import { tabOfStatus } from "../../utils/candidateFunnel";
import { experienceLabel } from "../../utils/hhLabels";
import { telegramHref } from "../../utils/candidateMapper";
import {
    appliedText, formatMonths, formatPeriod, parseWorkHistory, placeMonths, totalMonths, waitingText,
} from "../../utils/candidateResume";

/**
 * Candidates as a list, like the hh.ru responses page (правка №4/№14, доработка 03.11). [Claude]
 * A card per candidate: header (name, response, waiting time, badges, status), the user's blocks
 * in the user's order (ИИ-оценка, опыт, места работы, где живёт, навыки, образование, языки,
 * контакты, комментарий), then quick actions. «Компактно» keeps one line per block.
 */
export const STATUS_PILL = {
    red: "bg-rose-50 text-rose-800 border-rose-200",
    amber: "bg-amber-50 text-amber-900 border-amber-200",
    green: "bg-emerald-50 text-emerald-800 border-emerald-200",
    grey: "bg-slate-100 text-slate-700 border-slate-200",
};

export function StatusPill({ candidate }) {
    const title = statusLabel(candidate.status, candidate.current_status_date || candidate.status_date) || "Нет статуса";
    const cls = `inline-block max-w-[220px] truncate rounded-md border px-2 py-0.5 text-xs font-medium ${STATUS_PILL[statusTone(candidate.status)]}`;
    const testType = completedTestTypeForStatus(candidate.status);
    return testType ? (
        <a href={`/tests/results?candidate_id=${encodeURIComponent(candidate.id)}&type=${testType}`}
            onClick={(e) => e.stopPropagation()} className={`${cls} hover:underline`} title="Открыть результаты тестов">{title}</a>
    ) : <span className={cls} title={title}>{title}</span>;
}

export function aiTone(score) {
    if (score >= 75) return "bg-emerald-100 text-emerald-800 border-emerald-200";
    if (score >= 50) return "bg-amber-100 text-amber-800 border-amber-200";
    return "bg-slate-100 text-slate-600 border-slate-200";
}

export function AiScore({ value, big = false }) {
    if (value === null || value === undefined || value === "") return null;
    const score = Math.round(Number(value));
    if (!Number.isFinite(score)) return null;
    return <span className={`shrink-0 rounded-md border font-bold ${aiTone(score)} ${big ? "px-2 py-1 text-sm" : "px-1.5 py-0.5 text-xs"}`}
        title="ИИ-оценка резюме, 0–100">ИИ {score}</span>;
}

const ageText = (age) => {
    const n = Number(age);
    if (!Number.isFinite(n) || n <= 0) return null;
    const mod10 = n % 10;
    const mod100 = n % 100;
    const word = mod10 === 1 && mod100 !== 11 ? "год" : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? "года" : "лет";
    return `${n} ${word}`;
};

function Row({ label, children, compact }) {
    return <div className={`grid gap-x-4 border-t border-slate-100 ${compact ? "grid-cols-[130px_1fr] py-1.5" : "grid-cols-[150px_1fr] py-2.5"}`}>
        <div className="text-sm text-slate-500">{label}</div>
        <div className="min-w-0 text-sm text-slate-800">{children}</div>
    </div>;
}

function Chips({ items, limit }) {
    const [all, setAll] = useState(false);
    const shown = all ? items : items.slice(0, limit);
    return <div className="flex flex-wrap gap-1">
        {shown.map((item) => <span key={item} className="rounded-md bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700">{item}</span>)}
        {items.length > limit ? <button type="button" onClick={(e) => { e.stopPropagation(); setAll((v) => !v); }}
            className="rounded-md px-1.5 py-0.5 text-xs text-sky-700 hover:bg-sky-50">{all ? "свернуть" : `ещё ${items.length - limit}`}</button> : null}
    </div>;
}

function Jobs({ places, compact }) {
    const [open, setOpen] = useState(false);
    const [more, setMore] = useState(false);
    if (!places.length) return <span className="text-slate-400">не указаны</span>;
    if (compact) {
        const p = places[0];
        return <span className="truncate">{[p.company, p.position, formatPeriod(p)].filter(Boolean).join(" · ")}{places.length > 1 ? <span className="text-slate-400"> · ещё {places.length - 1}</span> : null}</span>;
    }
    const shown = more ? places : places.slice(0, 2);
    return <div className="space-y-2">
        {shown.map((p, i) => <div key={`${p.company}-${i}`}>
            <div className="font-semibold uppercase tracking-tight text-slate-900">{p.company || "Компания не указана"}</div>
            <div className="text-slate-700">{[p.position, formatPeriod(p), formatMonths(placeMonths(p))].filter(Boolean).join(" • ")}</div>
            {i === 0 && p.description ? <>
                <button type="button" onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }} className="text-sm text-sky-700 hover:underline">
                    {open ? "Свернуть" : "Подробнее о последнем месте"} {open ? "▴" : "▾"}
                </button>
                {open ? <p className="mt-1 whitespace-pre-line text-sm text-slate-600">{p.description}</p> : null}
            </> : null}
        </div>)}
        {places.length > 2 ? <button type="button" onClick={(e) => { e.stopPropagation(); setMore((v) => !v); }} className="text-sm text-sky-700 hover:underline">
            {more ? "Только последние места" : `Ещё места работы: ${places.length - 2}`}
        </button> : null}
    </div>;
}

function AiBlock({ candidate, compact }) {
    const [open, setOpen] = useState(false);
    if (candidate.ai_score === null || candidate.ai_score === undefined) return <span className="text-slate-400">ещё не оценён</span>;
    const comment = String(candidate.ai_comment || "").trim();
    return <div className="flex items-start gap-2">
        <AiScore value={candidate.ai_score} big={!compact} />
        {comment ? <div className="min-w-0 flex-1">
            <p className={`text-sm text-slate-700 ${open ? "whitespace-pre-line" : compact ? "truncate" : "line-clamp-2"}`}>{comment}</p>
            {!compact && comment.length > 180 ? <button type="button" onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
                className="text-xs text-sky-700 hover:underline">{open ? "свернуть" : "почему такой балл"}</button> : null}
        </div> : null}
    </div>;
}

function Contacts({ candidate }) {
    const items = [];
    if (candidate.phone_number) items.push(<a key="p" href={`tel:${String(candidate.phone_number).replace(/[^\d+]/g, "")}`} onClick={(e) => e.stopPropagation()} className="font-medium text-slate-900 hover:text-sky-700">{candidate.phone_number}</a>);
    if (candidate.email) items.push(<a key="e" href={`mailto:${candidate.email}`} onClick={(e) => e.stopPropagation()} className="text-sky-700 hover:underline">{candidate.email}</a>);
    if (candidate.telegram_username) {
        const href = telegramHref(candidate.telegram_username);
        items.push(href ? <a key="t" href={href} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-sky-700 hover:underline">Telegram {candidate.telegram_username}</a> : <span key="t">Telegram {candidate.telegram_username}</span>);
    }
    if (candidate.hh_resume_link) items.push(<a key="h" href={candidate.hh_resume_link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-sky-700 hover:underline">Резюме на hh.ru</a>);
    if (!items.length) return <span className="text-slate-400">нет</span>;
    return <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">{items.flatMap((node, i) => (i ? [<span key={`s${i}`} className="text-slate-300">•</span>, node] : [node]))}</div>;
}

export const LIST_BLOCK_LABELS = {
    ai: "Оценка ИИ",
    experience: "Общий опыт",
    jobs: "Места работы",
    location: "Где живёт",
    skills: "Навыки",
    education: "Образование",
    languages: "Языки",
    contacts: "Контакты",
    comment: "Комментарий HR",
};

function Block({ name, candidate, places, compact }) {
    switch (name) {
    case "ai":
        return <Row label="Оценка ИИ" compact={compact}><AiBlock candidate={candidate} compact={compact} /></Row>;
    case "experience": {
        const months = totalMonths(places);
        const text = months ? formatMonths(months) : candidate.total_work_expirience ? experienceLabel(candidate.total_work_expirience) : "";
        const tenure = Number(candidate.average_service_length);
        return <Row label="Общий опыт" compact={compact}>
            {text || <span className="text-slate-400">не указан</span>}
            {Number.isFinite(tenure) && tenure > 0 ? <span className="text-slate-500"> · в среднем {String(tenure).replace(".", ",")} г. на одном месте</span> : null}
        </Row>;
    }
    case "jobs":
        return <Row label="Места работы" compact={compact}><Jobs places={places} compact={compact} /></Row>;
    case "location": {
        const parts = [candidate.city, ageText(candidate.age),
            candidate.salary_expectations > 0 ? `ждёт ${Number(candidate.salary_expectations).toLocaleString("ru-RU")} ₽` : null].filter(Boolean);
        return <Row label="Где живёт" compact={compact}>{parts.length ? parts.join(" • ") : <span className="text-slate-400">не указано</span>}</Row>;
    }
    case "skills": {
        const skills = (candidate.hard_skills || []).filter(Boolean);
        if (!skills.length) return null;
        return <Row label="Навыки" compact={compact}>{compact ? <span className="truncate">{skills.slice(0, 8).join(", ")}{skills.length > 8 ? "…" : ""}</span> : <Chips items={skills} limit={10} />}</Row>;
    }
    case "education": {
        const list = (candidate.education || []).filter(Boolean);
        if (!list.length) return null;
        return <Row label="Образование" compact={compact}>{compact ? <span className="truncate">{list.join("; ")}</span> : list.map((e) => <div key={e}>{e}</div>)}</Row>;
    }
    case "languages": {
        const list = (candidate.languages || []).filter(Boolean);
        if (!list.length) return null;
        return <Row label="Языки" compact={compact}>{list.join(", ")}</Row>;
    }
    case "contacts":
        return <Row label="Контакты" compact={compact}><Contacts candidate={candidate} /></Row>;
    default:
        return null;
    }
}

function CommentEditor({ initial, onSave, onCancel }) {
    const [text, setText] = useState(initial || "");
    const [saving, setSaving] = useState(false);
    return <div className="flex items-start gap-2" onClick={(e) => e.stopPropagation()}>
        <textarea autoFocus rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Комментарий HR"
            className="min-w-0 flex-1 resize-none rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-amber-400" />
        <div className="flex flex-col gap-1">
            <button type="button" disabled={saving} onClick={async () => { setSaving(true); const ok = await onSave(text.trim()); setSaving(false); if (ok) onCancel(); }}
                className="rounded-lg bg-[#e0bb48] px-2.5 py-1 text-xs font-semibold text-black hover:bg-[#d4af3a] disabled:opacity-50">Сохранить</button>
            <button type="button" onClick={onCancel} className="rounded-lg bg-slate-100 px-2.5 py-1 text-xs text-slate-600 hover:bg-slate-200">Отмена</button>
        </div>
    </div>;
}

export function CandidateCard({
    candidate, selected, onToggle, onOpen, quickActions, archivedView, busy, onAction,
    blocks, density = "detailed", editingComment, onCommentClose, onCommentSave,
}) {
    const compact = density === "compact";
    const places = parseWorkHistory(candidate.other_work_expirience);
    const archived = archivedView || isArchiveStatus(candidate.status);
    const vacancy = candidate.vacancy_name || candidate.last_vacancy_title;
    const waiting = tabOfStatus(candidate.status) === "new" ? waitingText(candidate.applied_at) : "";
    const overdue = candidate.next_contact_at && new Date(candidate.next_contact_at).getTime() <= Date.now();
    const visible = (blocks || []).filter((b) => b !== "comment");
    const showComment = (blocks || []).includes("comment");

    return <li data-testid="candidate-row"
        className={`group cursor-pointer rounded-2xl border bg-white transition-colors ${selected ? "border-amber-400 ring-1 ring-amber-300" : "border-slate-200 hover:border-emerald-300"} ${compact ? "px-4 py-3" : "px-5 py-4"}`}
        onClick={(e) => {
            if (e.target.closest("button, a, input, textarea, select, label")) return;
            onOpen(candidate);
        }}>
        <div className="flex gap-4">
            {!compact ? <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-slate-100 text-2xl font-bold text-slate-400">
                {candidate.full_name ? candidate.full_name.charAt(0).toUpperCase() : "К"}
            </div> : null}
            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                            <button type="button" onClick={() => onOpen(candidate)}
                                className={`text-left font-semibold text-sky-700 hover:underline ${compact ? "text-base" : "text-lg"}`}>
                                {candidate.full_name || "Без имени"}
                            </button>
                            {candidate.is_perfect_candidate ? <span className="text-amber-500" title="Идеальный кандидат">★</span> : null}
                            {compact ? <AiScore value={candidate.ai_score} /> : null}
                        </div>
                        <div className="mt-0.5 text-sm text-emerald-700">
                            {appliedText(candidate.applied_at, vacancy)}
                            {candidate.resume_update_date ? <span className="text-slate-400"> • резюме обновлено {String(candidate.resume_update_date).toLowerCase()} назад</span> : null}
                        </div>
                    </div>
                    <StatusPill candidate={candidate} />
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1.5 text-xs">
                    {waiting ? <span className="rounded-md bg-slate-100 px-2 py-0.5 text-slate-700">{waiting}</span> : null}
                    {candidate.active_search ? <span className="rounded-md bg-emerald-50 px-2 py-0.5 text-emerald-700">Активно ищет работу</span> : null}
                    {candidate.next_contact_at ? <span className={`rounded-md px-2 py-0.5 ${overdue ? "bg-rose-50 font-semibold text-rose-700" : "bg-amber-50 text-amber-800"}`}>
                        {overdue ? "Пора связаться" : "Следующий контакт"}: {new Date(candidate.next_contact_at).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                    </span> : null}
                    {candidate.is_not_sync ? <span className="rounded-md border border-rose-200 bg-rose-50 px-2 py-0.5 font-semibold text-rose-700" title="Статус на HH.ru отличается от статуса в платформе">не синхронизирован с hh</span> : null}
                </div>
            </div>
        </div>

        {visible.length ? <div className={compact ? "mt-2" : "mt-3"}>
            {visible.map((name) => <Block key={name} name={name} candidate={candidate} places={places} compact={compact} />)}
        </div> : null}

        {editingComment ? <div className="mt-2 border-t border-slate-100 pt-2">
            <CommentEditor initial={candidate.hr_comment} onCancel={onCommentClose} onSave={(text) => onCommentSave(candidate, text)} />
        </div> : showComment && candidate.hr_comment ? <div className="mt-2 border-t border-slate-100 pt-2">
            <div className={`rounded-lg bg-yellow-50/80 px-3 py-1.5 text-sm text-yellow-900 ${compact ? "truncate" : "whitespace-pre-line"}`} title={candidate.hr_comment}>
                {candidate.hr_comment}
            </div>
        </div> : null}

        <div className={`flex flex-wrap items-center gap-2 ${compact ? "mt-2" : "mt-3"}`}>
            <label className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white" onClick={(e) => e.stopPropagation()}>
                <input type="checkbox" checked={selected} onChange={() => onToggle(candidate.id)}
                    aria-label={`Выбрать ${candidate.full_name || "кандидата"}`} className="h-4 w-4 accent-amber-500" />
            </label>
            <CandidateQuickActionButtons candidate={candidate} keys={quickActions} archived={archived} busy={busy} onAction={onAction} />
        </div>
    </li>;
}

export default function CandidateListView({
    candidates, selected, onToggle, onOpen, quickActions, archivedView, busyId, onAction,
    commentFor, onCommentClose, onCommentSave, blocks, density,
}) {
    return <ul className={`grid ${density === "compact" ? "gap-2" : "gap-3"}`} data-testid="candidates-list-view">
        {candidates.map((candidate) => <CandidateCard
            key={candidate.id}
            candidate={candidate}
            selected={selected.has(candidate.id)}
            onToggle={onToggle}
            onOpen={onOpen}
            quickActions={quickActions}
            archivedView={archivedView}
            busy={busyId === candidate.id}
            onAction={onAction}
            blocks={blocks}
            density={density}
            editingComment={commentFor === candidate.id}
            onCommentClose={onCommentClose}
            onCommentSave={onCommentSave}
        />)}
    </ul>;
}
